/**
 * メールアドレスの形式の検査（送る前に、サーバと同じ規則で確かめる）
 *
 * サーバは pydantic の EmailStr（email-validator 2.3.0、届くかどうかの確認はしない設定）で検査する。
 * 合わないと 422 になり、英語の検査の文言しか返らないため、画面で先に止めて日本語で知らせる。
 * 規則は email-validator の syntax.py（validate_email_local_part・validate_email_domain_name・validate_email_length）に合わせる。
 * 画面で再現できない入力（英数字・記号以外の文字、xn-- のラベル、引用符・山括弧）はサーバに任せ、ここでは止めない
 * （画面だけ厳しくして、サーバが受け付けるアドレスを止めないため）。
 * email-validator の版を上げるときは、単体テストの突き合わせの一覧をサーバの結果と照らし直す。
 */

export const EMAIL_FORMAT_ERROR_MESSAGE = 'メールアドレスの形式が正しくありません。';

// RFC 5322 3.2.3 の atext（email-validator の ATEXT と同じ）
const ATEXT = "a-zA-Z0-9_!#$%&'*+\\-/=?^`{|}~";
const LOCAL_PART_RE = new RegExp(`^[${ATEXT}]+(?:\\.[${ATEXT}]+)*$`);
const HOSTNAME_LABEL_RE = /^(?:[a-zA-Z0-9][a-zA-Z0-9-]*)?[a-zA-Z0-9]$/;
// 3〜4 文字目が "--" のラベル（xn-- を除く。email-validator の R-LDH の検査）
const RESERVED_LDH_LABEL_RE = /^(?!xn)..--/i;
const SPECIAL_USE_DOMAIN_NAMES = ['arpa', 'invalid', 'local', 'localhost', 'onion', 'test'];
// ドメインの上限（253 文字）は、全体の上限（254 文字）を満たせば必ず満たすため、別に検査しない
// （ローカル部と @ で 2 文字以上あるため、ドメインが 253 を超えると全体が 254 を超える）
const EMAIL_MAX_LENGTH = 254;
const DNS_LABEL_LENGTH_LIMIT = 63;
// ASCII 以外の文字（日本語のドメイン等）、引用符・山括弧（"表示名" <アドレス> の形はサーバが受け付ける）を含むものは、
// 画面では判定しない。ASCII の空白・制御文字は下の規則で止める（サーバも受け付けない）
const ASCII_MAX_CODE = 0x7f;
const DEFER_TO_SERVER_CHARS_RE = /["<>]/;

function isDeferredToServer(email) {
    return DEFER_TO_SERVER_CHARS_RE.test(email) || Array.from(email).some((c) => c.charCodeAt(0) > ASCII_MAX_CODE);
}

function isDomainValid(domain) {
    const lower = domain.toLowerCase();
    const labels = lower.split('.');
    if (labels.length < 2) return false; // ピリオドを 1 つ以上含む
    if (!labels.every((label) => label.length <= DNS_LABEL_LENGTH_LIMIT && HOSTNAME_LABEL_RE.test(label))) {
        return false;
    }
    if (labels.some((label) => RESERVED_LDH_LABEL_RE.test(label))) return false;
    if (!/[a-z]$/.test(lower)) return false; // 最上位ドメインの末尾は英字
    return !SPECIAL_USE_DOMAIN_NAMES.some((name) => lower === name || lower.endsWith(`.${name}`));
}

/**
 * メールアドレスを送ってよいか
 *
 * @param {string} value - 入力された値
 * @returns {boolean} サーバの検査に合う（または画面では判定せずサーバに任せる）なら true
 */
export function isEmailFormatAcceptable(value) {
    const email = String(value ?? '').trim();
    if (isDeferredToServer(email)) return true;
    const at = email.indexOf('@');
    if (at < 0) return false;
    const local = email.slice(0, at);
    const domain = email.slice(at + 1);
    if (/(^|\.)xn--/i.test(domain)) return true; // IDNA の検証は画面では再現できない
    if (email.length > EMAIL_MAX_LENGTH) return false;
    return LOCAL_PART_RE.test(local) && isDomainValid(domain);
}
